import { useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useCohortKeys } from 'components/Views/Hooks/dataHooks';
import { useParsedCohortQueryParam } from 'components/hooks/useParsedCohortQueryParam';
import { useDispatch } from 'react-redux';
import {
  VIEWS_ACTIONS,
  setActiveTab,
  setBaseCohort,
  setFilterSidebarState,
  useActiveTab,
  useCheckedBaseCohort,
  useFilterSidebarState,
} from 'reducers/viewsSlice';
import { checkCustomRenameLocationToSubsidiaryEnabled } from 'utils/helpers/featureFlags';

import { VIEWS_FILTER_KEYS } from 'constants/ProjectConstants';

import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import {
  MenuDropdown,
  MenuDropdownButton,
  MenuDropdownItem,
  MenuDropdownPanel,
} from 'uiToolkitV1/MenuDropdown/MenuDropdown';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextLabel } from 'uiToolkitV1/Typography';

const StyledInnerButton = styled(ListItemComp)`
  padding: 12px 36px !important;
`;

const StyledBox = styled(Box)`
  padding: 8px;
  overflow: hidden;
`;

const getSectionFilters = (allFilters) => {
  const nameAndTeamsIds = ['managers'];
  const attributesIds = [
    'grade',
    'roleId',
    'functionId',
    // 'department',
    'location',
    'gender',
    'ethnicity',
    'talentPartnerEmail',
  ];
  const nameAndteamFilters = allFilters.filter((filter) => nameAndTeamsIds.includes(filter.id));
  let attributeFilters = allFilters.filter((filter) => attributesIds.includes(filter.id));

  // replacing "location"'s displayName in attributeFilters
  attributeFilters = attributeFilters.map((filter) => {
    if (filter.id === 'location') {
      return {
        ...filter,
        displayName: checkCustomRenameLocationToSubsidiaryEnabled() ? 'Subsidiary' : 'Location',
      };
    }
    return filter;
  });

  return {
    nameAndteamFilters,
    attributeFilters,
  };
};

export const FilterSidebar = ({ viewAction }) => {
  const activeTab = useActiveTab();
  const dispatch = useDispatch();
  const isEditCustomAccess = viewAction === VIEWS_ACTIONS.EDIT_CUSTOM_ACCESS;
  const { data: keys, isLoading } = useCohortKeys({
    pageType: isEditCustomAccess ? 'STATUS' : null,
  });
  const queryCohort = useParsedCohortQueryParam();
  const checkedBaseCohort = useCheckedBaseCohort();

  const filters = getSectionFilters(keys ?? []);

  const accordionState = useFilterSidebarState();

  const toggleAccordionState = (itemIndex) => {
    dispatch(setFilterSidebarState(itemIndex));
  };

  useEffect(() => {
    if (!checkedBaseCohort?.uuid) {
      setBaseCohort(queryCohort?.id);
    }
  }, [queryCohort, checkedBaseCohort?.uuid]);

  const selectFirstChild = (category) => {
    // todo: raj remove when bottom todo is also resolved
    const tempFilters = {
      ...filters,
      nameAndteamFilters: [
        { id: VIEWS_FILTER_KEYS.EMPLOYEES, displayName: 'Employees' },
        ...filters.nameAndteamFilters,
      ],
    };
    // if (category === 'nameAndteamFilters') {
    //   if (activeTab === VIEWS_FILTER_KEYS.EMPLOYEES) return;
    //   dispatch(setActiveTab(VIEWS_FILTER_KEYS.EMPLOYEES));
    //   return;
    // }
    if (tempFilters[category].some((filter) => filter.id === activeTab)) return;

    const filter = tempFilters[category]?.[0];
    dispatch(setActiveTab(filter?.id));
  };

  useEffect(() => {
    if (isEditCustomAccess) {
      if (keys?.length > 0) {
        if (accordionState.findIndex((item) => item === 0) === -1) {
          toggleAccordionState(0);
        }
        selectFirstChild('attributeFilters');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditCustomAccess, keys]);

  // useEffect(() => {

  //   const hasNameAndTeamFilters = filters?.nameAndteamFilters?.length > 0;
  //   const hasAttributeFilters = filters?.attributeFilters?.length > 0;
  //   const hasPerformanceFilters = filters?.performanceFilters?.length > 0;

  // }, []);

  return (
    <StyledBox>
      {isEditCustomAccess ? (
        <TextLabel bold p="8px 4px">
          SELECT USER FILTERS
        </TextLabel>
      ) : (
        <TextLabel bold p="8px 4px">
          SELECT A BASE SET & USER FILTERS
        </TextLabel>
      )}
      {!isEditCustomAccess && (
        <ListItemComp
          active={activeTab === VIEWS_FILTER_KEYS.BASE_COHORT}
          onClick={() => dispatch(setActiveTab(VIEWS_FILTER_KEYS.BASE_COHORT))}
          title="BASE SETS"
          isBoldText={false}
        />
      )}
      <MenuDropdown index={accordionState} defaultIndex={[1]}>
        {!isEditCustomAccess && (
          <MenuDropdownItem>
            <MenuDropdownButton
              onClick={() => {
                toggleAccordionState(0);
                !accordionState.includes(0) && selectFirstChild('nameAndteamFilters');
              }}
            >
              Name & Teams
            </MenuDropdownButton>

            <MenuDropdownPanel>
              {/* todo: raj avoid this special handling, get this from BE only like other filters */}
              {isLoading ? (
                <SkeletonLoader skeletons={['46px']} my={1} />
              ) : (
                <StyledInnerButton
                  active={activeTab === VIEWS_FILTER_KEYS.EMPLOYEES}
                  onClick={() => dispatch(setActiveTab(VIEWS_FILTER_KEYS.EMPLOYEES))}
                  title="Name"
                  isBoldText={false}
                />
              )}

              {isLoading ? (
                <SkeletonLoader skeletons={['46px']} my={1} />
              ) : (
                filters?.nameAndteamFilters?.map((filter) => (
                  <StyledInnerButton
                    active={activeTab === filter.id}
                    onClick={() => dispatch(setActiveTab(filter.id))}
                    title={filter.displayName}
                    isBoldText={false}
                  />
                ))
              )}
            </MenuDropdownPanel>
          </MenuDropdownItem>
        )}

        {isLoading ? (
          <SkeletonLoader skeletons={['46px']} my={1} />
        ) : (
          filters?.attributeFilters?.length > 0 && (
            <MenuDropdownItem>
              <MenuDropdownButton
                onClick={() => {
                  toggleAccordionState(isEditCustomAccess ? 0 : 1);
                  !accordionState.includes(isEditCustomAccess ? 0 : 1) && selectFirstChild('attributeFilters');
                }}
              >
                Attributes
              </MenuDropdownButton>
              <MenuDropdownPanel>
                {filters?.attributeFilters?.map((filter) => (
                  <StyledInnerButton
                    active={activeTab === filter.id}
                    onClick={() => dispatch(setActiveTab(filter.id))}
                    title={filter.displayName}
                    isBoldText={false}
                  />
                ))}
              </MenuDropdownPanel>
            </MenuDropdownItem>
          )
        )}
      </MenuDropdown>
    </StyledBox>
  );
};
